// src/pages/Teto_vs_egen/ChatAnalysis.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./ChatAnalysis.css";

const PASSWORD = "chugumi"; // 비밀번호

export default function ChatAnalysis() {
    const [step, setStep] = useState("button"); // button | login | chat
    const [inputPwd, setInputPwd] = useState("");
    const [sessionId] = useState(() => crypto.randomUUID());
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // timer & analysis control
    const [timeLeft, setTimeLeft] = useState(5 * 60); // 5 minutes
    const [analysisRequested, setAnalysisRequested] = useState(false);
    const timerRef = useRef(null);

    const startChat = () => setStep("login");

    const handleLogin = (e) => {
        e.preventDefault();
        if (inputPwd === PASSWORD) {
            setStep("chat");
        } else {
            alert("비밀번호가 틀렸습니다.");
        }
    };

    // Start countdown when chat begins
    useEffect(() => {
        if (step === "chat") {
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        if (!analysisRequested) requestAnalysis();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timerRef.current);
    }, [step]);

    const sendMessage = async () => {
        if (!userInput.trim() || analysisRequested) return;
        const userMsg = { role: "user", content: userInput };
        setMessages((m) => [...m, userMsg]);
        setUserInput("");
        try {
            const chatRes = await axios.post("/analysis/chat", {
                session_id: sessionId,
                message: userMsg,
            });
            const assistantMsg = { role: "assistant", content: chatRes.data.reply };
            setMessages((m) => [...m, assistantMsg]);
        } catch (err) {
            setError(err.response?.data?.detail || err.message);
        }
    };

    const requestAnalysis = async () => {
        setLoading(true);
        setError(null);
        setAnalysisRequested(true);
        try {
            // Use the new profile endpoint
            const res = await axios.post("/analysis/personality_profile", {
                messages: messages // Send full chat history
            });
            setAnalysis(res.data);
            setStep("result"); // Switch to result screen

            // Auto-save if logged in
            try {
                const { savePersonalityResult } = await import('../../client');
                savePersonalityResult(res.data);
            } catch (saveErr) {
                console.warn("Failed to auto-save result", saveErr);
            }

        } catch (err) {
            setError(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };



    return (
        <div className="chat-analysis-wrapper">
            {step === "button" && (
                <div className="intro-screen">
                    <button className="start-chat-button" onClick={startChat}>
                        📊 성향 분석 채팅 시작
                    </button>
                </div>
            )}

            {step === "login" && (
                <form className="login-form" onSubmit={handleLogin}>
                    <h3>🔐 비밀번호 입력</h3>
                    <input
                        type="password"
                        value={inputPwd}
                        onChange={(e) => setInputPwd(e.target.value)}
                        placeholder="비밀번호"
                        className="pwd-input"
                    />
                    <button type="submit" className="pwd-submit">
                        확인
                    </button>
                </form>
            )}

            {step === "chat" && (
                <div className="chat-overlay">
                    {/* Header / Timer */}
                    <div className="chat-header">
                        <div className="timer-container">
                            <span className="timer-icon">⏳</span>
                            <span className="timer-text">
                                {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
                            </span>
                        </div>
                        <div className="progress-bar-container">
                            <div className="progress-bar-fill" style={{ width: `${(timeLeft / (5 * 60)) * 100}%` }}></div>
                        </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="messages-container">
                        {messages.map((m, i) => (
                            <div key={i} className={`message-bubble ${m.role}`}>
                                <div className="message-content">{m.content}</div>
                            </div>
                        ))}
                        {/* Dummy div to scroll to bottom if needed */}
                        <div style={{ float: "left", clear: "both" }} ></div>
                    </div>

                    {/* Input Area */}
                    <div className="input-section">
                        <textarea
                            rows={1}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            placeholder="메시지를 입력하세요..."
                            disabled={analysisRequested || timeLeft === 0}
                        />
                        <button onClick={requestAnalysis} disabled={loading || analysisRequested || timeLeft === 0} className="analyze-btn">
                            {loading ? "분석 중..." : "분석 요청"}
                        </button>
                    </div>

                    {/* Error Message Display */}
                    {error && (
                        <div className="error-toast">
                            ⚠️ {error}
                        </div>
                    )}

                    {/* Analysis Result Modal - REMOVED from here, moved to separate step */}
                </div>
            )}

            {step === "result" && analysis && (
                <div className="analysis-screen-wrapper">
                    <div className="analysis-container fade-in">
                        <div className="result-header">
                            <h2>✨ 당신의 성향 분석 보고서</h2>
                            <button className="restart-btn" onClick={() => window.location.reload()}>처음으로</button>
                        </div>

                        <div className="result-content">
                            {/* MBTI Section */}
                            <section className="analysis-section mbti-section">
                                <h3>🧭 MBTI: <span className="highlight">{analysis.mbti.type}</span></h3>
                                <div className="mbti-breakdown">
                                    {Object.entries(analysis.mbti.breakdown).map(([key, score]) => (
                                        <div key={key} className="stat-row">
                                            <span className="stat-label">{key}</span>
                                            <div className="stat-bar-bg">
                                                <div className="stat-bar-fill" style={{ width: `${score}%`, backgroundColor: score > 50 ? '#4c51bf' : '#a0aec0' }}></div>
                                            </div>
                                            <span className="stat-value">{Math.round(score)}%</span>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* Big 5 Section */}
                            <section className="analysis-section big5-section">
                                <h3>📊 Big 5 성향</h3>
                                <div className="big5-grid">
                                    {Object.entries(analysis.big5).map(([trait, score]) => (
                                        <div key={trait} className="stat-row">
                                            <span className="stat-label">{trait}</span>
                                            <div className="stat-bar-bg">
                                                <div className="stat-bar-fill" style={{ width: `${score}%`, background: 'linear-gradient(90deg, #38a169, #48bb78)' }}></div>
                                            </div>
                                            <span className="stat-value">{Math.round(score)}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* Keywords */}
                            <section className="analysis-section">
                                <h3>🔑 핵심 키워드</h3>
                                <div className="keywords-container">
                                    {analysis.keywords.map((k, i) => (
                                        <span key={i} className="keyword-tag">#{k}</span>
                                    ))}
                                </div>
                            </section>

                            {/* Recommendations */}
                            <section className="analysis-section rec-section">
                                <h3>💡 추천</h3>
                                <div className="rec-grid">
                                    <div className="rec-group">
                                        <h4>💼 추천 직업</h4>
                                        <ul className="list">
                                            {analysis.recommendations.jobs.map((j, i) => <li key={i}>{j}</li>)}
                                        </ul>
                                    </div>
                                    <div className="rec-group">
                                        <h4>🎨 추천 취미</h4>
                                        <ul className="list">
                                            {analysis.recommendations.hobbies.map((h, i) => <li key={i}>{h}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
